import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { type StayCategory } from '../../../__generated__/graphql';
import { Alert, EmptyState } from '../../components/ui';
import { useSearchStaysQuery } from '../../services/stayService/__generated__/queries.generated';
import { CategoryBar, categoryLabel, useScrollArrows } from './StayCategoryBar';
import { SectionTitle, StayCard, StayGrid } from './StayGuestComponents';
import { EMPTY_SEARCH, homeRows, type HomeRow } from './stayHomeRows';
import { StaySearchBar } from './StaySearchBar';
import { EMPTY_FILTERS, staySearchQuery, toSearchStaysInput } from './staySearchState';

/** Airbnb's page gutter: 24px on phones, 40px from tablets, wide pages up to 1760px. */
export const Gutter = styled.div`
  width: 100%;
  max-width: 1760px;
  margin: 0 auto;
  padding: 0 24px;
  @media (min-width: 744px) {
    padding: 0 40px;
  }
`;

const SearchBand = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 0 24px;
  background: var(--color-bg-elevated, #fff);
  @media (max-width: 743px) {
    padding: 12px 0 8px;
  }
`;

const CategoryStrip = styled.div`
  position: sticky;
  top: 0;
  z-index: 30;
  padding-top: 12px;
  background: var(--color-bg-elevated, #fff);
  border-bottom: 1px solid var(--color-border);
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02);
`;

const Main = styled.main`
  padding: 32px 0 80px;
  display: flex;
  flex-direction: column;
  gap: 48px;
`;

const RowHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`;

const RowTitle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: var(--color-text-primary);
  text-align: left;
  &:hover h2 {
    text-decoration: underline;
  }
`;

const RowArrows = styled.div`
  display: flex;
  gap: 8px;
  @media (max-width: 743px) {
    display: none;
  }
`;

const RoundArrow = styled.button`
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-border);
  border-radius: 9999px;
  background: var(--color-bg-elevated, #fff);
  color: var(--color-text-primary);
  cursor: pointer;
  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
  &:not(:disabled):hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
  }
`;

const Track = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 16px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  > * {
    scroll-snap-align: start;
  }
  /* Cards per screen: 1.6 on phones, then 3, 4, 5, 6 and 7 like Airbnb's home rows. */
  grid-auto-columns: 62%;
  @media (min-width: 744px) {
    grid-auto-columns: calc((100% - 2 * 16px) / 3);
  }
  @media (min-width: 950px) {
    grid-auto-columns: calc((100% - 3 * 16px) / 4);
  }
  @media (min-width: 1128px) {
    grid-auto-columns: calc((100% - 4 * 16px) / 5);
  }
  @media (min-width: 1440px) {
    grid-auto-columns: calc((100% - 5 * 16px) / 6);
  }
  @media (min-width: 1640px) {
    grid-auto-columns: calc((100% - 6 * 16px) / 7);
  }
`;

function Chevron({ left = false, size = 12 }: { left?: boolean; size?: number }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      aria-hidden="true"
    >
      <path d={left ? 'M10 2 4 8l6 6' : 'M6 2l6 6-6 6'} />
    </svg>
  );
}

function CardRow({ row, onOpen }: { row: HomeRow; onOpen: (id: string) => void }) {
  const navigate = useNavigate();
  const { ref, edges, scroll } = useScrollArrows<HTMLDivElement>();
  const go = row.search
    ? () => navigate(`/stays/search?${staySearchQuery(row.search!)}`)
    : undefined;
  return (
    <section data-testid={`stay-home-row-${row.key}`}>
      <RowHead>
        {go ? (
          <RowTitle type="button" onClick={go}>
            <SectionTitle>{row.title}</SectionTitle>
            <Chevron size={14} />
          </RowTitle>
        ) : (
          <SectionTitle>{row.title}</SectionTitle>
        )}
        <RowArrows>
          <RoundArrow
            type="button"
            aria-label="Previous"
            disabled={!edges.left}
            onClick={() => scroll(-1)}
          >
            <Chevron left />
          </RoundArrow>
          <RoundArrow
            type="button"
            aria-label="Next"
            disabled={!edges.right}
            onClick={() => scroll(1)}
          >
            <Chevron />
          </RoundArrow>
        </RowArrows>
      </RowHead>
      <Track ref={ref}>
        {row.items.map(item => (
          <StayCard
            key={item.listing.id}
            listing={item.listing}
            onOpen={() => onOpen(item.listing.id)}
          />
        ))}
      </Track>
    </section>
  );
}

function SkeletonGrid() {
  return (
    <StayGrid aria-busy="true" aria-label="Finding places">
      {Array.from({ length: 10 }, (_, n) => (
        <div key={n}>
          <div
            style={{
              aspectRatio: '20 / 19',
              borderRadius: 12,
              background: 'var(--color-bg-secondary)',
              marginBottom: 12,
            }}
          />
          <div
            style={{
              height: 14,
              width: '70%',
              borderRadius: 4,
              background: 'var(--color-bg-secondary)',
            }}
          />
          <div
            style={{
              height: 14,
              width: '45%',
              borderRadius: 4,
              background: 'var(--color-bg-secondary)',
              marginTop: 8,
            }}
          />
        </div>
      ))}
    </StayGrid>
  );
}

/** The stays site's home (spec 016 W11), laid out like Airbnb's: search, categories, rows of places. */
export default function StayHomePage() {
  const navigate = useNavigate();
  const [category, setCategory] = useState<StayCategory | undefined>();
  const state = useMemo(
    () => ({ ...EMPTY_SEARCH, filters: { ...EMPTY_FILTERS, category } }),
    [category],
  );
  const { data, loading, error } = useSearchStaysQuery({
    variables: { input: toSearchStaysInput(state, 1, 48) },
    fetchPolicy: 'cache-and-network',
  });
  const items = data?.searchStays.items ?? [];
  const rows = category ? [] : homeRows(items);
  const open = (id: string) => navigate(`/stays/${id}`);

  return (
    <>
      <SearchBand>
        <Gutter style={{ display: 'flex', justifyContent: 'center' }}>
          <StaySearchBar initial={EMPTY_SEARCH} />
        </Gutter>
      </SearchBand>
      <CategoryStrip>
        <Gutter>
          <CategoryBar value={category} onChange={setCategory} />
        </Gutter>
      </CategoryStrip>
      <Gutter>
        <Main>
          {error && !data && (
            <Alert tone="error" title="Could not load places">
              Try again in a moment.
            </Alert>
          )}
          {loading && !data ? (
            <SkeletonGrid />
          ) : items.length === 0 ? (
            <EmptyState
              title={
                category
                  ? `No ${categoryLabel(category).toLowerCase()} places yet`
                  : 'No places yet'
              }
              description="Check back soon, or try another category."
            />
          ) : rows.length ? (
            rows.map(row => <CardRow key={row.key} row={row} onOpen={open} />)
          ) : (
            <section>
              <RowHead>
                <SectionTitle>{category ? categoryLabel(category) : 'Places to stay'}</SectionTitle>
              </RowHead>
              <StayGrid>
                {items.map(item => (
                  <StayCard
                    key={item.listing.id}
                    listing={item.listing}
                    onOpen={() => open(item.listing.id)}
                  />
                ))}
              </StayGrid>
            </section>
          )}
        </Main>
      </Gutter>
    </>
  );
}
